import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  ShieldCheck,
  Calculator,
  ChevronRight,
  Award,
  Globe,
  TrendingUp,
  FileCheck,
  LineChart,
} from "lucide-react";

// Counter component for animated numbers
const AnimatedCounter = ({ target, suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime = null;
          const startValue = 0;
          const endValue = target;

          const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = Math.floor(
              startValue + (endValue - startValue) * easeOutQuart,
            );
            setCount(currentValue);
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(endValue);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 },
    );

    // Copy ref value to local variable for cleanup
    const currentElement = elementRef.current;

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [target, duration, hasAnimated]);

  return (
    <span ref={elementRef}>
      {count}
      {suffix}
    </span>
  );
};

export default function Home() {
  // Rolling text states - replaces "practice"
  const rollingWords = ["Integrity.", "Strategy.", "Execution.", "Practise."];
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const [isRolling, setIsRolling] = useState(false);
  const [nextWordIndex, setNextWordIndex] = useState(1);

  // Endless rolling animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setIsRolling(true);
      const nextIndex = (activeWordIndex + 1) % rollingWords.length;
      setNextWordIndex(nextIndex);
      setTimeout(() => {
        setActiveWordIndex(nextIndex);
        setIsRolling(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [activeWordIndex, rollingWords.length]);

  // Services data - 7 services total
  const services = [
    {
      icon: BarChart3,
      title: "Comprehensive Financial Reporting",
      desc: "We deliver customized financial reports for operational and strategic needs, including detailed P&L segmentation.",
      link: "/expertise#financial-reporting",
    },
    {
      icon: Calculator,
      title: "Operational Financial Insights",
      desc: "Detailed reporting structures that help track business performance, identify trends, and improve decision-making.",
      link: "/expertise#operational-insights",
    },
    {
      icon: FileCheck,
      title: "Management Reporting & Analysis",
      desc: "Advanced management reports designed to support leadership decisions, highlight risks, and uncover growth opportunities.",
      link: "/expertise#management-reporting",
    },
    {
      icon: TrendingUp,
      title: "Strategic Financial Intelligence",
      desc: "Transform financial data into actionable insights for long-term planning, forecasting, and strategic execution.",
      link: "/expertise#strategic-intelligence",
    },
    {
      icon: BarChart3,
      title: "Profit & Loss Segmentation",
      desc: "Detailed P&L breakdowns with segment-level visibility for better operational control and performance tracking.",
      link: "/expertise#profit-loss",
    },
    {
      icon: ShieldCheck,
      title: "Cash Flow & Liquidity Reporting",
      desc: "Clear visibility into cash flow trends to ensure financial stability and support informed liquidity planning.",
      link: "/expertise#cashflow",
    },
    {
      icon: LineChart,
      title: "Balance Sheet Enhancement",
      desc: "Enhanced balance sheet reporting with improved clarity, structure, and financial transparency.",
      link: "/expertise#balance-sheet",
    },
  ];

  // Carousel state - for sliding one box at a time
  const [slideIndex, setSlideIndex] = useState(0);

  // Card dimensions
  const cardWidth = 280;
  const gap = 24;
  const slideDistance = cardWidth + gap;
  const totalServices = services.length;

  // Create extended array for seamless infinite loop (duplicate services to create endless effect)
  // We need enough copies to cover the visible window plus sliding distance
  // Only the first four cards are cloned (aria-hidden) so the last slide positions stay full.
  const VISIBLE_CARDS = 4;
  const extendedServices = [...services, ...services.slice(0, VISIBLE_CARDS)];

  // Calculate transform offset to show exactly 4 cards
  const getTransformOffset = () => {
    // Each slide moves by exactly one card width + gap
    const offset = slideIndex * slideDistance;
    return -offset;
  };

  // Auto-slide effect - moves one box every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => {
        const next = prev + 1;
        // When we reach the end of the original set, reset to 0 for seamless loop
        // This creates an endless loop effect
        if (next >= totalServices) {
          return 0;
        }
        return next;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [totalServices]);

  // Get current actual index for indicators (0-7)
  const getActualIndex = () => {
    return slideIndex % totalServices;
  };

  return (
    <div className="flex flex-col w-full bg-white pt-28">
      {/* Editorial Hero Section */}
      <section className="relative bg-[#203468] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="/images/photos/hero-corporate-architecture.webp"
            alt="Suspension bridge cables over New York City, a symbol of structure and precision"
            className="w-full h-full object-cover mix-blend-luminosity"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#203468] via-[#203468]/90 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-24 lg:py-40">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-300 text-xs font-semibold uppercase tracking-widest mb-8">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
              Leading Global Advisory
            </div>
            <h1 className="font-serif text-5xl lg:text-7xl font-normal leading-[1.1] mb-8">
              Tax, Audit &amp;{" "}
              <span className="italic text-blue-400">Accounting Support</span>{" "}
              for CPA Firms
            </h1>
            <div className="home-tagline">
              Clarity in{" "}
              <span className="italic text-blue-400">complexity.</span>
              <br />
              Precision in{" "}
              <span className="italic text-blue-400">
                <span className="relative inline-block min-w-[200px] text-left">
                  <span
                    className={`inline-block transition-all duration-300 ${
                      isRolling ? "animate-roll-out" : ""
                    }`}
                  >
                    {rollingWords[activeWordIndex]}
                  </span>
                  {isRolling && (
                    <span
                      className="absolute top-0 left-0 inline-block animate-roll-in"
                      style={{ color: "#60a5fa" }}
                    >
                      {rollingWords[nextWordIndex]}
                    </span>
                  )}
                </span>
              </span>
            </div>
            <p className="text-lg lg:text-xl text-slate-300 mb-12 max-w-2xl leading-relaxed font-light">
              The Alpha CPAs provides elite tax, audit, and accounting support
              for forward-thinking firms. We handle the technical execution so
              you can lead with confidence.{" "}
            </p>
            <div className="flex flex-col sm:flex-row gap-5">
              <Link
                to="/contact"
                className="bg-white text-[#203468] px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-blue-50 transition-all inline-flex items-center justify-center gap-3 shadow-xl"
              >
                Engage Our Firm <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/expertise"
                className="border border-slate-600 hover:border-white text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all inline-flex items-center justify-center"
              >
                Our Expertise
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes rollOut {
            0% { transform: translateY(0); opacity: 1; }
            100% { transform: translateY(-40px); opacity: 0; }
          }
          @keyframes rollIn {
            0% { transform: translateY(40px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          .animate-roll-out { animation: rollOut 0.3s ease-in forwards; }
          .animate-roll-in { animation: rollIn 0.3s ease-out forwards; }
          
          .carousel-track {
            display: flex;
            gap: 24px;
            transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            will-change: transform;
          }
          
          .carousel-container {
            overflow: hidden;
            width: 100%;
          }
        `}
      </style>

      {/* Prestige Stats Strip */}
      <section className="bg-blue-700 py-12 relative z-20 -mt-8 mx-4 sm:mx-8 lg:mx-auto max-w-7xl shadow-2xl overflow-hidden">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-blue-600/50">
          <div className="px-4">
            <p className="text-4xl lg:text-5xl font-serif text-white mb-2">
              <AnimatedCounter target={9} suffix="+" duration={2500} />
            </p>
            <p className="text-xs text-blue-200 uppercase tracking-[0.2em] font-semibold">
              Years Legacy
            </p>
          </div>
          <div className="px-4">
            <p className="text-4xl lg:text-5xl font-serif text-white mb-2">
              $<AnimatedCounter target={12} suffix="M" duration={2500} />
            </p>
            <p className="text-xs text-blue-200 uppercase tracking-[0.2em] font-semibold">
              Assets Advised
            </p>
          </div>
          <div className="px-4">
            <p className="text-4xl lg:text-5xl font-serif text-white mb-2">
              <AnimatedCounter target={3} duration={2500} />
            </p>
            <p className="text-xs text-blue-200 uppercase tracking-[0.2em] font-semibold">
              Global Offices
            </p>
          </div>
          <div className="px-4">
            <p className="text-4xl lg:text-5xl font-serif text-white mb-2">
              <AnimatedCounter target={500} suffix="+" duration={2500} />
            </p>
            <p className="text-xs text-blue-200 uppercase tracking-[0.2em] font-semibold">
              Enterprise Clients
            </p>
          </div>
        </div>
      </section>

      {/* Split Layout - Value Proposition */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="/images/photos/unsplash-bb046344.webp"
                  alt="Executives meeting around a boardroom table"
                  className="w-full h-80 object-cover rounded-sm"
                  referrerPolicy="no-referrer"
                />
                <img
                  src="/images/photos/unsplash-d954caaa.webp"
                  alt="Analyst reviewing financial charts"
                  className="w-full h-80 object-cover rounded-sm mt-16"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-4">
                The Growing Need for Outsourcing in
              </h2>
              <h3 className="font-serif text-4xl lg:text-5xl text-[#203468] mb-6 leading-tight">
                <span className="italic">CPA </span> Firms.
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed mb-8 font-light">
                The accounting landscape in the United States has evolved
                significantly over the past few years. CPA firms are facing
                increasing pressure from multiple directions tighter filing
                deadlines, growing client expectations, frequent regulatory
                changes, and a persistent shortage of skilled accounting
                professionals. At the same time, firms are expected to deliver
                faster turnaround times without compromising on accuracy or
                compliance.
              </p>
              <ul className="space-y-6 mb-10">
                {[
                  {
                    icon: Award,
                    title: "Precision & Compliance Excellence",
                    desc: "Multi-level review processes ensure every financial statement, tax return, and workpaper meets the highest standards of accuracy and regulatory compliance before final delivery.",
                  },
                  {
                    icon: Globe,
                    title: "Seamless System Integration",
                    desc: "We work within your existing ecosystem whether it's CCH, Drake, UltraTax, or QuickBooks, ensuring a smooth and disruption-free workflow.",
                  },
                  {
                    icon: TrendingUp,
                    title: "Scalable Financial Operations",
                    desc: "Quickly scale your team during peak tax seasons without the long-term overhead of hiring and training, while maintaining efficiency and turnaround time.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="bg-slate-50 p-3 rounded-sm text-blue-700">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="text-[#203468] font-bold mb-1">
                        {item.title}
                      </h4>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                className="group inline-flex items-center gap-2 text-[#203468] font-bold uppercase tracking-wider text-sm hover:text-blue-700 transition-colors"
              >
                Discover Our History{" "}
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Confidentiality Section */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-blue-500 text-xl font-medium mb-2 tracking-tight">
                Unwavering
              </h2>
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#203468] leading-tight mb-10 font-sans tracking-tight">
                Commitment to Data
                <br />
                Security & Confidentiality
              </h3>
              <div className="flex items-center gap-2">
                <img
                  src= "/images/iso/iso1.jpg"
                  alt="ISO 27001 Information Security Management certified badge"
                  className="w-20 h-20 md:w-30 md:h-30 object-contain"
                  referrerPolicy="no-referrer"
                />
                <img
                  src= "/images/iso/iso2.png"
                  alt="ISO 27701:2019 Privacy Information Management System certified badge"
                  className="w-20 h-20 md:w-30 md:h-30 object-contain"
                  referrerPolicy="no-referrer"
                />
                <img
                  src= "/images/iso/iso3.png"
                  alt="ISO 9001 certified badge"
                  className="w-20 h-20 md:w-30 md:h-30 object-contain"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
            <div>
              <h4 className="text-xl md:text-2xl font-semibold text-[#203468] mb-6 leading-snug font-sans tracking-tight">
                Data protection is not optional. It is foundational.
              </h4>
              <p className="text-slate-600 text-lg leading-relaxed font-light">
                Our{" "}
                <strong className="font-semibold text-[#203468]">
                  ISO/IEC 27001:2022 certification
                </strong>{" "}
                and{" "}
                <strong className="font-semibold text-[#203468]">
                  SOC 2 Type II
                </strong>{" "}
                compliance reflect our commitment to safeguarding sensitive
                client information across both digital and physical
                environments. Every process is built around strict access
                controls, secure infrastructure, and continuous monitoring,
                giving you confidence that your data is handled with the highest
                standards of security, privacy, and operational integrity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Carousel - Shows exactly 4 boxes at a time */}
      <section className="py-24 bg-slate-50 border-t border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-4">
                Areas of Practice
              </h2>
              <h3 className="font-serif text-4xl lg:text-5xl text-[#203468] leading-tight">
                Specialized expertise for complex challenges.
              </h3>
            </div>
            <Link
              to="/services"
              className="shrink-0 bg-[#203468] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-blue-700 transition-colors"
            >
              View All Services
            </Link>
          </div>

          {/* Carousel - Shows exactly 4 boxes */}
          <div className="carousel-container px-2 md:px-6">
            <div
              className="carousel-track items-stretch"
              style={{
                transform: `translateX(${getTransformOffset()}px)`,
                width: `${extendedServices.length * (cardWidth + gap)}px`,
              }}
            >
              {extendedServices.map((service, idx) => {
                const isClone = idx >= totalServices;
                const Title = isClone ? "p" : "h4";
                return (
                  <div
                    key={idx}
                    aria-hidden={isClone ? "true" : undefined}
                    className="flex-shrink-0 bg-white p-8 border border-slate-200 hover:border-blue-700 transition-all duration-300 group cursor-pointer flex flex-col h-full"
                    style={{ width: `${cardWidth}px` }}
                  >
                    <div className="text-slate-400 group-hover:text-blue-700 transition-colors mb-8">
                      <service.icon className="h-10 w-10 stroke-[1.5]" />
                    </div>
                    <Title className="text-xl font-serif font-bold text-[#203468] mb-4">
                      {service.title}
                    </Title>
                    <p className="text-slate-600 text-sm leading-relaxed mb-8">
                      {service.desc}
                    </p>
                    <Link
                      to={service.link}
                      tabIndex={isClone ? -1 : undefined}
                      className="text-blue-700 font-bold text-sm uppercase tracking-wider flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity mt-auto"
                    >
                      Explore <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Carousel Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {services.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setSlideIndex(idx);
                }}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === getActualIndex()
                    ? "w-8 bg-blue-700"
                    : "w-1.5 bg-slate-300 hover:bg-slate-400"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#203468] py-24 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('/images/photos/cta-pattern.webp')] bg-cover bg-center mix-blend-overlay" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6 leading-tight">
            Ready to elevate your financial strategy?
          </h2>
          <p className="text-slate-400 text-lg mb-10 font-light">
            Partner with The Alpha CPA's. for unparalleled financial expertise,
            strategic foresight, and dedicated service.
          </p>
          <Link
            to="/contact"
            className="bg-blue-600 text-white px-10 py-4 text-sm font-bold uppercase tracking-wider hover:bg-blue-500 transition-colors inline-block shadow-2xl shadow-blue-900/50"
          >
            Schedule a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
}